import type { KeyBindings, SettingsStore } from './SettingsStore';

export type InputAction =
  | 'moveUp'
  | 'moveDown'
  | 'moveLeft'
  | 'moveRight'
  | 'consume'
  | 'pause'
  | 'menu';

/**
 * Abstracts keycodes from gameplay-named actions. Reads bindings from
 * SettingsStore so rebinding in the menu takes effect live. UI code
 * never references raw keycodes — only Actions.
 *
 * Tracks edge-triggered presses (wasPressed) alongside held state
 * (isDown). Call beginFrame() once per tick before any reads.
 */
export class ActionMap {
  private downKeys = new Set<string>();
  private pressedThisFrame = new Set<string>();
  private previousDown = new Set<string>();

  private bindings: KeyBindings;
  private unsubSettings: (() => void) | null = null;

  constructor(initialBindings: KeyBindings) {
    this.bindings = initialBindings;
  }

  /** Subscribe to a SettingsStore so bindings update live on settings change. */
  connectSettings(store: SettingsStore): () => void {
    this.bindings = store.snapshot.controls.bindings;
    const off = store.onChange((settings) => {
      this.bindings = settings.controls.bindings;
    });
    this.unsubSettings = off;
    return off;
  }

  /** Call on KeyboardEvent keydown. Prefer event.code (physical key). */
  onKeyDown(code: string): void {
    this.downKeys.add(code);
  }

  /** Call on KeyboardEvent keyup. */
  onKeyUp(code: string): void {
    this.downKeys.delete(code);
  }

  /** Call at the start of every frame to compute edge-triggered events. */
  beginFrame(): void {
    this.pressedThisFrame.clear();
    for (const code of this.downKeys) {
      if (!this.previousDown.has(code)) {
        this.pressedThisFrame.add(code);
      }
    }
    this.previousDown = new Set(this.downKeys);
  }

  isActionDown(action: InputAction): boolean {
    for (const code of this.bindings[action]) {
      if (this.downKeys.has(code)) return true;
    }
    return false;
  }

  wasActionPressed(action: InputAction): boolean {
    for (const code of this.bindings[action]) {
      if (this.pressedThisFrame.has(code)) return true;
    }
    return false;
  }

  dispose(): void {
    this.unsubSettings?.();
    this.unsubSettings = null;
    this.downKeys.clear();
    this.pressedThisFrame.clear();
    this.previousDown.clear();
  }
}
